// component-product-media-slyde.js
if (!customElements.get('product-media-slyde')) {
  class ProductMediaSlyde extends HTMLElement {
    constructor() {
      super();
      this.slydeInstance = null;
      this.selectedVariantMediaId = null;
      this.product = null;
      this.isMobile = false;
    }

    async connectedCallback() {
      // Check if we're on mobile
      this.isMobile = window.matchMedia('(max-width: 739px)').matches;

      // Get elements
      this.carousel = this.querySelector('.product__media-carousel');
      this.grid = this.querySelector('.product__media-grid');
      this.viewInSpaceElement = this.querySelector('[data-shopify-model3d-id]');

      // Load product data
      try {
        this.product = await this.loadProduct(this.getAttribute('product-handle'));
      } catch (error) {
        console.error('Failed to load product:', error);
        return;
      }

      // Initialize Slyde carousel on mobile
      if (this.isMobile && this.carousel) {
        this.initializeSlyde();
      }

      // Setup grid video autoplay on desktop
      if (!this.isMobile && this.grid && this.hasAttribute('autoplay-video')) {
        this.setupGridVideoAutoplay();
      }

      // Listen for variant changes
      const formId = this.getAttribute('form-id');
      if (formId) {
        const form = document.getElementById(formId);
        if (form) {
          form.addEventListener('variant:changed', this.handleVariantChange.bind(this));
        }
      }

      // Handle window resize
      this.handleResize = this.debounce(() => {
        const wasMobile = this.isMobile;
        this.isMobile = window.matchMedia('(max-width: 739px)').matches;

        if (wasMobile !== this.isMobile) {
          if (this.isMobile) {
            // Switched to mobile
            this.destroyGridVideoObserver();
            if (!this.slydeInstance) {
              this.initializeSlyde();
            }
          } else {
            // Switched to desktop
            this.destroySlyde();
            if (this.hasAttribute('autoplay-video')) {
              this.setupGridVideoAutoplay();
            }
          }
        }
      }, 250);

      window.addEventListener('resize', this.handleResize);

      // Setup reveal on scroll if needed
      if (this.hasAttribute('reveal-on-scroll')) {
        this.setupVisibility();
      }
    }

    disconnectedCallback() {
      this.destroySlyde();
      this.destroyGridVideoObserver();
      if (this.handleResize) {
        window.removeEventListener('resize', this.handleResize);
      }
    }

    /**
     * Load product data from Shopify
     */
    async loadProduct(handle) {
      if (window.ProductLoader && typeof window.ProductLoader.load === 'function') {
        return await window.ProductLoader.load(handle);
      }

      // Fallback: fetch product data directly
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) {
        throw new Error(`Failed to load product: ${response.statusText}`);
      }
      return await response.json();
    }

    /**
     * Initialize Slyde carousel
     */
    initializeSlyde() {
      const SlydeClass = window.Slyde?.Slyde || window.Slyde?.default || window.Slyde;
      if (!this.carousel || typeof SlydeClass !== 'function') {
        console.warn('Slyde.js not loaded or carousel element not found');
        return;
      }

      const container = this.carousel.querySelector('.slyde-container');
      if (!container) {
        console.warn('Slyde container not found');
        return;
      }

      try {
        // Get filtered media items
        const visibleSlides = this.getAllMediaItems(this.carousel).filter(
          item => !item.classList.contains('is-filtered')
        );

        if (visibleSlides.length === 0) {
          console.warn('No visible slides found');
          return;
        }

        // Collect available modules from the Slyde namespace
        const SlydeNS = window.Slyde || {};
        const modules = [
          SlydeNS.Navigation,
          SlydeNS.Scrollbar,
          SlydeNS.Pagination,
          SlydeNS.Video
        ].filter(Boolean);

        // Initialize Slyde with settings - let Slyde handle video autoplay
        this.slydeInstance = new SlydeClass(container, {
          modules: modules,
          loop: visibleSlides.length > 1,
          autoplay: false,
          navigation: true,
          scrollbar: true,
          pagination: visibleSlides.length > 1,
          thumbnails: false,
          videoAutoplay: this.hasAttribute('autoplay-video'),
          video: true,
          overscrollBehavior: true
        });

        // Listen for slide changes and update (loop correction)
        if (this.slydeInstance) {
          this.slydeInstance.on('slideChange', () => {
            this.handleSlideChange();
          });
          this.slydeInstance.on('update', () => {
            this.handleSlideChange();
          });
        }
      } catch (error) {
        console.error('Failed to initialize Slyde:', error);
      }
    }

    /**
     * Destroy Slyde instance
     */
    destroySlyde() {
      if (this.slydeInstance && typeof this.slydeInstance.destroy === 'function') {
        this.slydeInstance.destroy();
        this.slydeInstance = null;
      }
    }

    /**
     * Setup video autoplay for desktop grid using IntersectionObserver
     */
    setupGridVideoAutoplay() {
      const videos = this.grid.querySelectorAll('video');
      if (!videos.length) return;

      this.gridVideoObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          const video = entry.target;
          if (entry.isIntersecting) {
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      }, {
        threshold: 0.5
      });

      videos.forEach(video => {
        this.gridVideoObserver.observe(video);
      });
    }

    /**
     * Destroy grid video observer
     */
    destroyGridVideoObserver() {
      if (this.gridVideoObserver) {
        this.gridVideoObserver.disconnect();
        this.gridVideoObserver = null;
      }
    }

    /**
     * Handle variant change event
     */
    async handleVariantChange(event) {
      const variant = event.detail.variant;
      const isSwatchChange = event.detail.isSwatchChange || false;
      if (!variant || !this.product) return;

      const filteredMediaIds = this.getFilteredMediaIds(variant);
      const currentlyFilteredIds = this.getCurrentlyFilteredIds();

      // Check if we need to update filtering
      const needsUpdate = currentlyFilteredIds.some(id => !filteredMediaIds.includes(id)) ||
                         filteredMediaIds.some(id => !currentlyFilteredIds.includes(id));

      if (needsUpdate) {
        this.updateMediaFiltering(filteredMediaIds, variant);

        // Reinitialize Slyde on mobile if needed
        if (this.isMobile) {
          this.destroySlyde();
          // Small delay to allow DOM updates
          setTimeout(() => this.initializeSlyde(), 100);
        } else if (this.grid && variant.featured_media && isSwatchChange) {
          // Scroll to featured image on desktop only for swatch changes
          this.scrollToVariantImage(variant.featured_media.id);
        }
      } else if (variant.featured_media && variant.featured_media.id !== this.selectedVariantMediaId && isSwatchChange) {
        // Just select the featured media (only for swatch changes)
        this.selectMedia(variant.featured_media.id, variant);
      }

      this.selectedVariantMediaId = variant.featured_media ? variant.featured_media.id : null;
    }

    /**
     * Get filtered media IDs based on variant
     */
    getFilteredMediaIds(variant) {
      const filteredIds = [];

      if (!this.product || !this.product.media) return filteredIds;

      this.product.media.forEach(media => {
        const isVariantMedia = variant.featured_media && media.id === variant.featured_media.id;

        if (media.alt && media.alt.includes('#') && !isVariantMedia) {
          const altParts = media.alt.split('#');
          const mediaGroupParts = altParts[altParts.length - 1].split('_');

          this.product.options.forEach(option => {
            if (option.name.toLowerCase() === mediaGroupParts[0].toLowerCase()) {
              const variantOptionValue = variant.options[option.position - 1];
              const mediaOptionValue = mediaGroupParts[1].trim();

              if (variantOptionValue.toLowerCase() !== mediaOptionValue.toLowerCase()) {
                filteredIds.push(media.id);
              }
            }
          });
        }
      });

      return filteredIds;
    }

    /**
     * Get currently filtered media IDs from DOM
     */
    getCurrentlyFilteredIds() {
      const filteredItems = this.querySelectorAll('.is-filtered[data-media-id]');
      return Array.from(filteredItems).map(item => parseInt(item.getAttribute('data-media-id')));
    }

    /**
     * Update media filtering in DOM
     */
    updateMediaFiltering(filteredMediaIds, variant) {
      const allMediaItems = this.getAllMediaItems();
      const selectedMediaId = variant.featured_media
        ? variant.featured_media.id
        : this.getFirstVisibleMediaId(filteredMediaIds);

      allMediaItems.forEach(item => {
        const mediaId = parseInt(item.getAttribute('data-media-id'));
        const isFiltered = filteredMediaIds.includes(mediaId);
        const isSelected = mediaId === selectedMediaId;

        item.classList.toggle('is-filtered', isFiltered);
        item.classList.toggle('is-selected', isSelected);
        item.classList.toggle('is-initial-selected', isSelected);
      });
    }

    /**
     * Get first visible (non-filtered) media ID
     */
    getFirstVisibleMediaId(filteredMediaIds) {
      if (!this.product || !this.product.media) return null;

      const visibleMedia = this.product.media.find(media => !filteredMediaIds.includes(media.id));
      return visibleMedia ? visibleMedia.id : null;
    }

    /**
     * Get all media items from both carousel and grid
     */
    getAllMediaItems(container = null) {
      const selector = '.product__media-item[data-media-id]';
      return container
        ? Array.from(container.querySelectorAll(selector))
        : Array.from(this.querySelectorAll(selector));
    }

    /**
     * Select a specific media item by ID
     */
    selectMedia(mediaId, variant = null) {
      if (this.isMobile && this.slydeInstance) {
        // Find visible items, EXCLUDING cloned slides (loop mode creates clones)
        const visibleItems = this.getAllMediaItems(this.carousel).filter(
          item => !item.classList.contains('is-filtered') &&
                  !item.closest('.slyde-slide-clone')
        );
        const index = visibleItems.findIndex(
          item => parseInt(item.getAttribute('data-media-id')) === mediaId
        );

        if (index >= 0 && typeof this.slydeInstance.goTo === 'function') {
          this.slydeInstance.goTo(index);
        }
      } else if (!this.isMobile && this.grid) {
        // Desktop grid handling - scroll to the image
        this.scrollToVariantImage(mediaId);
      }
    }

    /**
     * Scroll to variant image on desktop
     */
    scrollToVariantImage(featuredMediaId) {
      const targetItem = this.grid?.querySelector(`[data-media-id="${featuredMediaId}"]`);
      if (targetItem) {
        targetItem.scrollIntoView({ behavior: 'smooth', block: 'start' });
        // Update selected state
        this.grid.querySelectorAll('.product__media-item').forEach(item => {
          item.classList.toggle('is-selected', parseInt(item.getAttribute('data-media-id')) === featuredMediaId);
        });
      }
    }

    /**
     * Reorder desktop grid to show all variant images at top
     */
    reorderGridForVariant(featuredMediaId, variant) {
      if (!this.grid) return;

      // Get all non-filtered grid items
      const allItems = Array.from(
        this.grid.querySelectorAll('.product__media-item:not(.is-filtered)')
      );

      if (allItems.length === 0) return;

      // Separate into: featured, variant-matching, and other items
      let featuredItem = null;
      const variantItems = [];
      const otherItems = [];

      allItems.forEach(item => {
        const itemMediaId = parseInt(item.getAttribute('data-media-id'));

        if (itemMediaId === featuredMediaId) {
          featuredItem = item;
        } else if (variant && this.isMediaMatchingVariant(itemMediaId, variant)) {
          variantItems.push(item);
        } else {
          otherItems.push(item);
        }
      });

      // Get the first "other" item as insertion point
      const insertBeforeElement = otherItems[0] || null;

      // Move variant items to top (insert before the first "other" item)
      variantItems.forEach(item => {
        this.grid.insertBefore(item, insertBeforeElement);
      });

      // Move featured item to very top
      if (featuredItem) {
        this.grid.insertBefore(featuredItem, this.grid.firstElementChild);
      }
    }

    /**
     * Check if a media item matches the variant's color option
     */
    isMediaMatchingVariant(mediaId, variant) {
      if (!this.product || !this.product.media || !variant) return false;

      const media = this.product.media.find(m => m.id === mediaId);
      if (!media || !media.alt || !media.alt.includes('#')) return false;

      const altParts = media.alt.split('#');
      const mediaGroupParts = altParts[altParts.length - 1].split('_');
      const optionName = mediaGroupParts[0];
      const mediaOptionValue = mediaGroupParts[1] ? mediaGroupParts[1].trim() : '';

      const matchingOption = this.product.options.find(
        option => option.name.toLowerCase() === optionName.toLowerCase()
      );

      if (!matchingOption) return false;

      const variantOptionValue = variant.options[matchingOption.position - 1];
      return variantOptionValue.toLowerCase() === mediaOptionValue.toLowerCase();
    }

    /**
     * Handle slide change in carousel
     */
    handleSlideChange() {
      if (!this.slydeInstance) return;

      const currentIndex = this.slydeInstance.currentIndex;
      const slideElement = this.slydeInstance.slideElements?.[currentIndex];
      const currentSlide = slideElement
        ? slideElement.querySelector('.product__media-item[data-media-id]')
        : null;

      if (!currentSlide) return;

      const mediaType = currentSlide.getAttribute('data-media-type');
      const mediaId = currentSlide.getAttribute('data-media-id');

      // Update view in space button for 3D models
      if (this.viewInSpaceElement) {
        if (mediaType === 'model') {
          this.viewInSpaceElement.setAttribute('data-shopify-model3d-id', mediaId);
        } else {
          const defaultId = this.viewInSpaceElement.getAttribute('data-shopify-model3d-default-id');
          if (defaultId) {
            this.viewInSpaceElement.setAttribute('data-shopify-model3d-id', defaultId);
          }
        }
      }

      // Handle video and model playback on slide change
      if (this.hasAttribute('autoplay-video') && ['video', 'external_video', 'model'].includes(mediaType)) {
        this.pauseAllMedia();
        if (mediaType === 'video') {
          const video = currentSlide.querySelector('video');
          if (video) {
            video.play().catch(() => {});
          }
        } else {
          const mediaElement = currentSlide.querySelector('external-video, model-media');
          if (mediaElement && typeof mediaElement.play === 'function') {
            mediaElement.play();
          }
        }
      } else {
        this.pauseAllMedia();
      }
    }

    /**
     * Pause all videos and models
     */
    pauseAllMedia() {
      // Pause native video elements
      const allVideos = this.querySelectorAll('video');
      allVideos.forEach(video => {
        video.pause();
      });

      // Pause external videos and models (custom elements)
      const allCustomMedia = this.querySelectorAll('external-video, model-media');
      allCustomMedia.forEach(element => {
        if (typeof element.pause === 'function') {
          element.pause();
        }
      });
    }

    /**
     * Setup reveal on scroll animation
     */
    async setupVisibility() {
      // This would integrate with the theme's reveal system
      // For now, we'll just ensure images are visible
      if (typeof this.untilVisible === 'function') {
        await this.untilVisible();
      }
    }

    /**
     * Debounce utility
     */
    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    }
  }

  customElements.define('product-media-slyde', ProductMediaSlyde);
}
